import React, { useEffect } from "react";
import "@/App.css";
import { Route, Routes, BrowserRouter, useLocation } from "react-router-dom";
import axios from "axios";
import StrollerQuiz from "@/pages/StrollerQuiz";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";
import "@/config"; // side-effect: injects --bayou-art CSS var, sets axios defaults
import { AdminApp, AuthProvider } from "@/Admin";
import { LoginPage, AuthCallback, CustomerAccount, EmployeeApp, SetPassword, MagicAuth } from "@/UserApp";
import { ReservationDetail } from "@/pages/customer/ReservationDetail";
import { SavedCards } from "@/pages/customer/SavedCards";
import { SiteContentProvider } from "@/context/SiteContent";
import { CursorSparkle } from "@/components/effects/CursorSparkle";
import { Home } from "@/pages/Home";
import { BookingSuccess } from "@/pages/BookingSuccess";
import { CheckoutPage } from "@/pages/CheckoutPage";
import { ExtraChargePage } from "@/pages/ExtraChargePage";
import { PhotoGallery } from "@/pages/PhotoGallery";
import { BlogArticle } from "@/pages/BlogArticle";
import PrivacyPolicy from "@/pages/legal/PrivacyPolicy";
import TermsOfService from "@/pages/legal/TermsOfService";
import { TrackDriver } from "@/pages/TrackDriver";
import { Deals } from "@/pages/Deals";
import PartnerApply from "@/pages/partners/PartnerApply";
import PartnerLogin from "@/pages/partners/PartnerLogin";
import PartnerDashboard from "@/pages/partners/PartnerDashboard";
import ProductGuide from "@/pages/ProductGuide";
// SEO pages
import Strollers from "@/pages/Strollers";
import StrollerProduct from "@/pages/StrollerProduct";
import BabyGear from "@/pages/BabyGear";
import ECVs from "@/pages/ECVs";
import ECVProduct from "@/pages/ECVProduct";
import DisneyWorldStrollers from "@/pages/seo/DisneyWorldStrollers";
import StrollerRentalsOrlando from "@/pages/seo/StrollerRentalsOrlando";
import DoubleStrollerRentals from "@/pages/seo/DoubleStrollerRentals";
import SingleStrollerRentals from "@/pages/seo/SingleStrollerRentals";
import UniversalOrlandoStrollers from "@/pages/seo/UniversalOrlandoStrollers";
import LastMinuteStrollerRental from "@/pages/seo/LastMinuteStrollerRental";
import CribRentalDisney from "@/pages/seo/CribRentalDisney";
import BabyGearRentalDisney from "@/pages/seo/BabyGearRentalDisney";
import ZoeStrollerRental from "@/pages/seo/ZoeStrollerRental";
import BabyJoggerStrollerRental from "@/pages/seo/BabyJoggerStrollerRental";
import NunaStrollerRental from "@/pages/seo/NunaStrollerRental";
import BabyBrezzaRental from "@/pages/seo/BabyBrezzaRental";
import About from "@/pages/About";
import FAQPage from "@/pages/FAQPage";
import Contact from "@/pages/Contact";
import DeliveryHub from "@/pages/DeliveryHub";
import DisneyDelivery from "@/pages/delivery/DisneyDelivery";
import UniversalDelivery from "@/pages/delivery/UniversalDelivery";
import VacationHomeDelivery from "@/pages/delivery/VacationHomeDelivery";
import NotFound from "@/pages/NotFound";
import { initGA4, trackPageView } from "@/utils/ga4";

// Legacy re-export so existing consumers of `useSiteContent`/`T` from `@/App` keep working.
export { useSiteContent, T } from "@/context/SiteContent";

/* Scrolls to the top of the page on every route change.
   Without this, navigating from a hash-anchored section (e.g. #catalog) to a
   route page (e.g. /strollers) leaves the browser at the previous scroll position. */
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

/* Handles ?ref=TOKEN referral link attribution */
const ReferralTracker = () => {
  const location = useLocation();
  // Track SPA route changes in GA4 (no-op when GA4 is not configured)
  useEffect(() => {
    // Defer one tick so Helmet has updated document.title before we read it.
    const t = setTimeout(() => {
      trackPageView(location.pathname + location.search, document.title);
    }, 0);
    return () => clearTimeout(t);
  }, [location]);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ref = params.get("ref");
    if (!ref) return;
    // Get or create a session ID for click dedup
    let sid = localStorage.getItem("_egr_sid");
    if (!sid) {
      sid = Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem("_egr_sid", sid);
    }
    const BACKEND = process.env.REACT_APP_BACKEND_URL;
    axios.get(`${BACKEND}/api/partners/ref/${encodeURIComponent(ref)}?session_id=${sid}`)
      .then(r => {
        if (r.data.ok && r.data.code) {
          // Store referral so checkout can auto-apply the partner code
          sessionStorage.setItem("_egr_ref", JSON.stringify({
            referral_token: ref,
            code: r.data.code,
            partner_name: r.data.partner_name,
            discount_description: r.data.discount_description,
            stored_at: Date.now(),
          }));
        }
      })
      .catch(() => {});
  }, [location.search]);
  return null;
};

/* If the URL has ?session_id=... in the fragment, route to AuthCallback first. */
const AppRouter = () => {
  if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      {/* Stroller catalog and product pages */}
      <Route path="/strollers" element={<Strollers />} />
      <Route path="/strollers/:slug" element={<StrollerProduct />} />
      {/* Baby gear catalog */}
      <Route path="/gear" element={<BabyGear />} />
      {/* ECV catalog and product pages */}
      <Route path="/ecvs" element={<ECVs />} />
      <Route path="/ecvs/:slug" element={<ECVProduct />} />
      {/* SEO landing pages — stroller category */}
      <Route path="/disney-world-stroller-rentals" element={<DisneyWorldStrollers />} />
      <Route path="/stroller-rentals-orlando" element={<StrollerRentalsOrlando />} />
      <Route path="/double-stroller-rentals-orlando" element={<DoubleStrollerRentals />} />
      <Route path="/single-stroller-rentals-orlando" element={<SingleStrollerRentals />} />
      <Route path="/universal-orlando-stroller-rentals" element={<UniversalOrlandoStrollers />} />
      <Route path="/last-minute-stroller-rental-disney-world" element={<LastMinuteStrollerRental />} />
      {/* SEO landing pages — brand pages */}
      <Route path="/zoe-stroller-rental-disney-world" element={<ZoeStrollerRental />} />
      <Route path="/baby-jogger-stroller-rental-disney-world" element={<BabyJoggerStrollerRental />} />
      <Route path="/nuna-stroller-rental-disney-world" element={<NunaStrollerRental />} />
      {/* SEO landing pages — baby gear */}
      <Route path="/crib-rental-disney-world" element={<CribRentalDisney />} />
      <Route path="/baby-gear-rental-disney-world" element={<BabyGearRentalDisney />} />
      <Route path="/baby-brezza-rental-orlando" element={<BabyBrezzaRental />} />
      {/* Information pages */}
      <Route path="/about" element={<About />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/contact" element={<Contact />} />
      {/* Delivery pages */}
      <Route path="/delivery" element={<DeliveryHub />} />
      <Route path="/delivery/walt-disney-world-resorts" element={<DisneyDelivery />} />
      <Route path="/delivery/universal-orlando" element={<UniversalDelivery />} />
      <Route path="/delivery/vacation-homes" element={<VacationHomeDelivery />} />
      {/* Existing routes */}
      <Route path="/" element={<Home />} />
      <Route path="/checkout" element={<CheckoutPage />} />
      <Route path="/checkout/extra" element={<ExtraChargePage />} />
      <Route path="/booking-success" element={<BookingSuccess />} />
      <Route path="/photos/:reservationId" element={<PhotoGallery />} />
      <Route path="/track/:token" element={<TrackDriver />} />
      <Route path="/journal/:slug" element={<BlogArticle />} />
      <Route path="/deals" element={<Deals />} />
      <Route path="/partners" element={<PartnerApply />} />
      <Route path="/partners/login" element={<PartnerLogin />} />
      <Route path="/partners/dashboard" element={<PartnerDashboard />} />
      <Route path="/partners/set-password" element={<SetPassword />} />
      <Route path="/guide/:productId" element={<ProductGuide />} />
      <Route path="/stroller-quiz" element={<StrollerQuiz />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/set-password" element={<SetPassword />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/auth/magic" element={<MagicAuth />} />
      <Route path="/account" element={<CustomerAccount />} />
      <Route path="/account/cards" element={<SavedCards />} />
      <Route path="/account/reservation/:id" element={<ReservationDetail />} />
      <Route path="/employee/*" element={<EmployeeApp />} />
      <Route path="/admin/*" element={<AdminApp />} />
      {/* 404 catch-all — must be last */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

function App() {
  // Initialize GA4 once on mount (no-op when REACT_APP_GA4_MEASUREMENT_ID is absent)
  useEffect(() => { initGA4(); }, []);
  return (
    <div className="App">
      {/* Global SVG filter for logo (used on booking-success page) */}
      <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden>
        <defs>
          <filter id="knockoutBlack" colorInterpolationFilters="sRGB">
            <feColorMatrix type="matrix" values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  2 2 2 0 -0.4" />
          </filter>
        </defs>
      </svg>
      <Toaster position="top-center" theme="dark" toastOptions={{ style: { background: "#0F1E2C", color: "#F6F1E4", border: "1px solid rgba(255,209,102,0.28)" } }} />
      <HelmetProvider>
        <BrowserRouter>
          <CursorSparkle />
          <AuthProvider>
            <SiteContentProvider>
              <ScrollToTop />
              <ReferralTracker />
              <AppRouter />
            </SiteContentProvider>
          </AuthProvider>
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
